// src/apolloClient.js
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { HttpLink } from '@apollo/client';
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";

// Create an HTTP link for queries and mutations
const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_BROKER_API_URL}/graphql`, // Replace with your GraphQL endpoint
});

// --- Connection status (subscribe from the app to react to drops) ---
// status: 'connecting' | 'connected' | 'disconnected'
let connectionStatus = 'connecting';
const statusListeners = new Set();

export const getConnectionStatus = () => connectionStatus;

export const onConnectionStatusChange = (cb) => {
  statusListeners.add(cb);
  cb(connectionStatus); // emit current value immediately
  return () => statusListeners.delete(cb);
};

const setConnectionStatus = (status) => {
  if (status === connectionStatus) return;
  connectionStatus = status;
  statusListeners.forEach((cb) => cb(status));
};

// How long to wait for a pong after we ping before declaring the socket dead.
const PONG_WAIT_MS = 5000;
let activeSocket;
let pongTimer;

// Create a WebSocket link for subscriptions
const wsClient = createClient({
  url: `${process.env.REACT_APP_BROKER_WS_API_URL}/graphql`, // Replace with your WebSocket endpoint
  keepAlive: 10000, // send a ping every 10s while idle so a dead socket reveals itself
  retryAttempts: Infinity, // keep trying to reconnect
  shouldRetry: () => true, // reconnect even on abnormal/unclean closes (firewall, NAT, sleep)
  on: {
    connecting: () => setConnectionStatus('connecting'),
    connected: (socket) => {
      activeSocket = socket;
      setConnectionStatus('connected');
    },
    closed: () => setConnectionStatus('disconnected'),
    error: () => setConnectionStatus('disconnected'),
    // Dead-socket detection: when we send a ping (received === false), expect a
    // pong shortly. If none arrives, the connection is silently dead (e.g. killed
    // by a firewall) — force-close it so the retry loop reconnects.
    ping: (received) => {
      if (!received) {
        clearTimeout(pongTimer);
        pongTimer = setTimeout(() => {
          setConnectionStatus('disconnected');
          if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
            activeSocket.close(4408, 'Request Timeout'); // triggers reconnect
          }
        }, PONG_WAIT_MS);
      }
    },
    // Server answered our ping: the socket is alive.
    pong: (received) => {
      if (received) clearTimeout(pongTimer);
    },
  },
});

const wsLink = new GraphQLWsLink(wsClient);

// Use split to direct requests to the appropriate link
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

// Create the Apollo Client
const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
});

export default client;
